import { createContext, useCallback, useContext, useEffect, useLayoutEffect } from "react";
import { fillUi, kindLabel, useGuideUi } from "../discoverSections";
import type { CountryNames, MapPoint } from "./data";

/**
 * The strings of the route map. They are the page's own dictionary (`GuideUiContext`, blog/<lang>.json: `guide.routeMap.*`), with the country's
 * name filled in where a string says it: `{country}`, and `{in}` `{the}` `{of}` `{by}` where the language needs them. The names are data: the
 * KB's route-map file for the country (data.ts), so a second country needs no change here.
 */

export const CountryNamesContext = createContext<CountryNames>({ country: "", in: "", the: "", of: "", by: "" });

type Vars = Record<string, string | number>;

/** `t("addStop", "Add a stop")`: the string `guide.routeMap.<key>` in the page's language, else the English one given. */
export function useRouteMapUi(): (key: string, fallback: string, vars?: Vars) => string {
	const ui = useGuideUi();
	const names = useContext(CountryNamesContext);
	return useCallback(
		(key, fallback, vars = {}) =>
			fillUi(ui(`guide.routeMap.${key}`, fallback), { ...names, ...Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, String(v)])) }),
		[ui, names]
	);
}

/** "2 nights": a count and the word for it, which the dictionary has apart (Ilse's `night` / `nights`) */
export const withUnit = (t: ReturnType<typeof useRouteMapUi>, n: number, one: string, other: string): string =>
	`${n} ${t(n === 1 ? one : other, n === 1 ? one : other)}`;

/** "3 stops": the dictionary's own singular and plural (`stopOne` / `stopMany`) */
export const stopsText = (t: ReturnType<typeof useRouteMapUi>, n: number): string =>
	n === 1 ? t("stopOne", "{count} stop", { count: n }) : t("stopMany", "{count} stops", { count: n });

/** The destination's type in the page's language: the KB's word for it (`types`), else the hub's own kind label. */
export function useTypeLabel(): (point: Pick<MapPoint, "type" | "typeLabel">) => string {
	const ui = useGuideUi();
	return useCallback((point) => (point.typeLabel && point.typeLabel !== point.type ? point.typeLabel : point.type ? kindLabel(point.type, ui) : ""), [ui]);
}

/** The locale dates and months are written in: the page's language, English the British way round, Portuguese the Portuguese way (Ilse's tone for pt) */
export const dateLocale = (lang: string): string => (lang === "en" ? "en-GB" : lang === "pt" ? "pt-PT" : lang);

/** A layout effect where there is a layout to measure; on the server, where React warns about it, an effect that never runs. */
export const useBrowserLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** The visitor asked for less motion: the routes do not take turns on the map and the camera jumps instead of flying. */
export const reducedMotion = (): boolean =>
	typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
